import { html, css, LitElement } from 'lit-element';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { clone, cloneDeep } from 'lodash-es';
import { library, dom } from "@fortawesome/fontawesome-svg-core";
import { doc, getDoc } from 'firebase/firestore';

import './teams_selector.js';
import './badge.js';

import { db } from './intrivity_app.js';
import { getScore } from './utility.js';
import { a11yStyles } from './a11y.js';
import { t, Localized } from './i18n.js';

export class IntrivityTeams extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            .teams {
                margin: 25px;
            }
            .group_list {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                grid-gap: 10px;
            }
            .group_badge {
                display: grid;
                grid-template-columns: 60px 1fr;
            }
            .scores {
                font-size: smaller;
                text-align: center;
                border-collapse: collapse;
            }
            .scores th, .scores td {
                padding: 0 4px;
                font-weight: normal;
            }
        `];
    }
    
    static get properties() {
        return {
            group_reports: { type: Array },
            group_report: { type: Object },
            size: { type: Number },
            _privacyFlags: { type: Object }
        };
    }

    constructor() {
        super();
        this.size = 1;
        this._privacyFlags = {};
    }

    // Read live from Firestore rather than the (possibly stale) cached
    // report JSON, so toggling a group/team's privacy setting takes effect
    // immediately without needing the report reprocessed.
    async updated(changedProps) {
        if (!changedProps.has('group_reports') || !this.group_reports) return;
        const flags = {};
        await Promise.all(this.group_reports.map(async (rep) => {
            if (!rep.group) return;
            try {
                const snap = await getDoc(doc(db, 'groups', rep.group));
                flags[rep.group] = snap.exists() && !!snap.data().disable_report_privacy;
            } catch (e) {
                console.warn('Could not load report privacy setting:', e);
                flags[rep.group] = false;
            }
        }));
        this._privacyFlags = flags;
    }

    render() {
        return html`
            <h1 class="sr-only">${t('teams.heading')}</h1>
            <intrivity-teams-selector .group_report=${this.group_report} .group_reports=${this.group_reports}></intrivity-teams-selector>
            <div class="teams">
        
                ${this.group_reports ? html`
                
                    ${this.group_reports.map((rep)=> html`
  
                        <h2>${rep.title}</h2>

                        <div class="group_list">

                            ${rep.users.sort((a, b) => {
                                if (a.lastName + ', ' + a.firstName < b.lastName + ', ' + b.firstName) {
                                  return -1;
                                }
                                if (a.lastName + ', ' + a.firstName > b.lastName + ', ' + b.firstName) {
                                  return 1;
                                }
                                return 0;
    }                       ).map((user=> html`
                                 <div class="group_badge">
                                    <intrivity-badge-plain aria-hidden="true" .m=${getScore(user.profile.m)} .r=${getScore(user.profile.r)} .s=${getScore(user.profile.s)} .t=${getScore(user.profile.t)} .size=${this.size}></intrivity-badge-plain>
                                    <div style="margin-left: 5px;">
                                        ${this._privacyFlags[rep.group] ? html`
                                            <div>${user.lastName}, ${user.firstName}</div>
                                        `: html``}
                                        <table class="scores">
                                            <caption class="sr-only">${t('teams.profileFor', { first: user.firstName, last: user.lastName })}</caption>
                                            <thead>
                                                <tr>
                                                    <th scope="col"><abbr title=${t('domain.message.name')}>M</abbr></th>
                                                    <th scope="col"><abbr title=${t('domain.relationship.name')}>R</abbr></th>
                                                    <th scope="col"><abbr title=${t('domain.time.name')}>T</abbr></th>
                                                    <th scope="col"><abbr title=${t('domain.space.name')}>S</abbr></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>${getScore(user.profile.m)}</td>
                                                    <td>${getScore(user.profile.r)}</td>
                                                    <td>${getScore(user.profile.t)}</td>
                                                    <td>${getScore(user.profile.s)}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ` ))}

                        </div>  
    
                    `)}

        
                `: html``}

            </div>
        `
    } 
 
}
if (!customElements.get('intrivity-teams')) customElements.define('intrivity-teams', IntrivityTeams);
